// ═══════════════════════════════════════════════════════════════════════════
//  FORMATTING
// ═══════════════════════════════════════════════════════════════════════════
// en-IN throughout. The audience is an Indian paint business, and 1,20,000
// is the number they read — a dashboard that says 120,000 makes them stop and
// convert, every time.

const NUM = new Intl.NumberFormat("en-IN");
const COMPACT = new Intl.NumberFormat("en-IN", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export function num(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  return NUM.format(Math.round(value));
}

/** For axis ticks and anywhere a full number would collide with its neighbour. */
export function compact(value: number): string {
  return Math.abs(value) >= 10_000 ? COMPACT.format(value) : NUM.format(value);
}

export function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  return `${value.toFixed(digits)}%`;
}

export function decimal(value: number, digits = 1): string {
  return Number.isFinite(value) ? value.toFixed(digits) : "—";
}

const DATE = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const DATETIME = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
});

export function date(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : DATE.format(d);
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : DATETIME.format(d);
}

/** `2026-08-14` → `14 Aug`. Axis ticks only — the year is in the range label. */
export function dayTick(day: string): string {
  const [, month, dayOfMonth] = day.split("-");
  const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${Number(dayOfMonth)} ${MONTHS[Number(month) - 1] ?? ""}`;
}

/** "3 days ago", "in 2 hours". Relative time is what a session list is read in. */
export function relative(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  const seconds = (d.getTime() - Date.now()) / 1000;
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  for (const [unit, secondsIn] of units) {
    if (Math.abs(seconds) >= secondsIn) {
      return rtf.format(Math.round(seconds / secondsIn), unit);
    }
  }
  return "just now";
}

/** HOME_OWNER → Home owner. The enum is shouted; the UI should not be. */
export function titleCase(value: string | null | undefined): string {
  if (!value) return "—";
  const words = value.replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * A user agent reduced to what a person recognises on a device list.
 *
 * Deliberately crude. The point is to tell one row from another — "is that my
 * phone or the one I left at the office" — not to build a device inventory,
 * and a full UA string in a table cell is unreadable noise.
 */
export function device(userAgent: string | null | undefined): string {
  if (!userAgent) return "Unknown device";

  const os = /iPhone|iPad/i.test(userAgent)
    ? "iOS"
    : /Android/i.test(userAgent)
      ? "Android"
      : /Windows/i.test(userAgent)
        ? "Windows"
        : /Mac OS X|Macintosh/i.test(userAgent)
          ? "macOS"
          : /Linux/i.test(userAgent)
            ? "Linux"
            : "Unknown OS";

  // Order matters: Edge and Chrome both claim "Chrome", Chrome claims
  // "Safari". Most specific first.
  const browser = /Edg\//i.test(userAgent)
    ? "Edge"
    : /OPR\/|Opera/i.test(userAgent)
      ? "Opera"
      : /Firefox\//i.test(userAgent)
        ? "Firefox"
        : /Chrome\//i.test(userAgent)
          ? "Chrome"
          : /Safari\//i.test(userAgent)
            ? "Safari"
            : "Browser";

  return `${browser} · ${os}`;
}

// USD, because that is what the model provider bills in — converting to rupees
// would bake an exchange rate into a number meant to be checked against an
// invoice. Sub-dollar amounts keep four decimals: one view costs a few cents,
// and "$0.0689" vs "$0.07" vs "$0.00" is the difference between a per-call
// figure you can multiply and one you cannot.
const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function usd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  if (value > 0 && value < 1) return `$${value.toFixed(4)}`;
  return USD.format(value);
}
